import { useCallback, useState } from 'react';
import {
  FormPageProperty,
  FileUploadProperty,
  FormPageInputItemFileUploadPiclib,
  FileUploadInputStatus,
  FILE_UPLOAD_INPUT_STATUS,
} from 'shared';
import {
  FileUploadItemState,
  FILE_UPLOAD_SESSION_STATE_TYPE,
  updateFileUploadItemState,
  useAuthentication,
  useFileUpload,
  useUpdateFileUploadItemStateDispatch,
} from 'store';
import {
  requestUploadedFile,
  REQUEST_UPLOADED_FILE_RESULT_TYPE,
} from './requestUploadedFile';

type UseUploadedFileHandlerPayload = {
  propertyId: FormPageProperty['propertyId'];
  subjectId: FileUploadProperty['inputFormat']['subjectId'];
  onUpdateNextState: (
    nextState: Extract<FileUploadInputStatus, 'ready' | 'hasFile'>,
  ) => void;
  onFileNotFound: () => void;
  onNeedsToReload: () => void;
};

type FileSrn = FormPageInputItemFileUploadPiclib['fileSrn'];
type NonNullableFileSrn = NonNullable<FileSrn>;

type PreparePayload = {
  fileSrn: FileSrn;
};

type ReloadPayload = {
  fileSrn: NonNullableFileSrn;
};

type UseUploadedFileHandlerReturnType = {
  sessionError: boolean;
  reloading: boolean;
  prepare: (payload: PreparePayload) => Promise<void>;
  reload: (payload: ReloadPayload) => Promise<void>;
};

export const useUploadedFileHandler = ({
  propertyId,
  subjectId,
  onUpdateNextState,
  onFileNotFound,
  onNeedsToReload,
}: UseUploadedFileHandlerPayload): UseUploadedFileHandlerReturnType => {
  const [sessionError, setSessionError] = useState(false);
  const [reloading, setReloading] = useState(false);
  const dispatch = useUpdateFileUploadItemStateDispatch();
  const fileUpload = useFileUpload();
  const fileUploadItem: FileUploadItemState | undefined =
    fileUpload.items[subjectId];
  const thumbnail = fileUploadItem?.thumbnail;
  const sessionType = fileUpload.session.type;
  const authentication = useAuthentication();

  const requestThumbnail = useCallback(
    async (fileSrn: NonNullableFileSrn) => {
      const result = await requestUploadedFile({
        fileSrn,
        propertyId,
        thumbnailApiEndpoint: fileUpload.setting?.thumbnailApiEndpoint,
        authentication,
      });

      if (result.type === REQUEST_UPLOADED_FILE_RESULT_TYPE.ok) {
        dispatch(
          updateFileUploadItemState({
            subjectId,
            awaiting: false,
            needsToReload: false,
            thumbnail: result.thumbnail,
          }),
        );
        onUpdateNextState(FILE_UPLOAD_INPUT_STATUS.hasFile);
        return;
      }

      if (result.type === REQUEST_UPLOADED_FILE_RESULT_TYPE.notFound) {
        onFileNotFound();
        if (fileUploadItem?.needsToReload) {
          dispatch(
            updateFileUploadItemState({
              subjectId,
              needsToReload: false,
            }),
          );
        }
        return;
      }

      if (result.type === REQUEST_UPLOADED_FILE_RESULT_TYPE.needsToReload) {
        onNeedsToReload();
        dispatch(
          updateFileUploadItemState({
            subjectId,
            needsToReload: true,
          }),
        );
        return;
      }
    },
    [
      authentication,
      dispatch,
      fileUpload.setting?.thumbnailApiEndpoint,
      fileUploadItem?.needsToReload,
      onFileNotFound,
      onNeedsToReload,
      onUpdateNextState,
      propertyId,
      subjectId,
    ],
  );

  const prepare = useCallback(
    async ({ fileSrn }: PreparePayload) => {
      const { error, notReady } = FILE_UPLOAD_SESSION_STATE_TYPE;

      if (sessionType === notReady) {
        return;
      }
      if (sessionType === error) {
        setSessionError(true);
        return;
      }
      if (!fileSrn) {
        onUpdateNextState(FILE_UPLOAD_INPUT_STATUS.ready);
        return;
      }
      if (thumbnail != null) {
        onUpdateNextState(FILE_UPLOAD_INPUT_STATUS.hasFile);
        return;
      }
      if (reloading) {
        return;
      }
      setReloading(true);
      await requestThumbnail(fileSrn);
      setReloading(false);
    },
    [onUpdateNextState, reloading, requestThumbnail, sessionType, thumbnail],
  );

  const reload = useCallback(
    async ({ fileSrn }: ReloadPayload) => {
      if (reloading) {
        return;
      }
      setReloading(true);
      await requestThumbnail(fileSrn);
      setReloading(false);
    },
    [reloading, requestThumbnail],
  );

  return {
    sessionError,
    prepare,
    reloading,
    reload,
  };
};
